import React, { useEffect, useRef } from 'react';

import { TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import {
  GoogleAddressComponent,
  ParsedAddress,
  buildAddressLabel,
  parseGoogleAddress,
} from './parse-google-address.util';

const MAPS_SCRIPT_URL = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_G_PLACES_KEY}&libraries=places`;

let scriptPromise: Promise<void> | null = null;

const loadMapsScript = (): Promise<void> => {
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = MAPS_SCRIPT_URL;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('maps'));
    document.head.appendChild(script);
  });

  return scriptPromise;
};

export const resetAddressAutocompleteMapsScriptForTests = (): void => {
  scriptPromise = null;
};

interface AddressAutocompleteProps {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  onAddressSelected: (address: ParsedAddress) => void;
  types?: string[];
  toLabel?: (address: ParsedAddress) => string;
  error?: boolean;
  helperText?: string;
  size?: 'small' | 'medium';
}

const DEFAULT_PLACE_TYPES = ['address'];

const useStyles = makeStyles({
  '@global': {
    '.pac-container': { zIndex: '9999 !important' },
  },
});

const AddressAutocomplete: React.FC<AddressAutocompleteProps> = ({
  id,
  label,
  value,
  onChange,
  onAddressSelected,
  types,
  toLabel = buildAddressLabel,
  error,
  helperText,
  size = 'medium',
}) => {
  useStyles();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const placeTypesKey = (types ?? DEFAULT_PLACE_TYPES).join(',');

  useEffect(() => {
    let isMounted = true;

    loadMapsScript()
      .then(() => {
        const places = (window as unknown as { google?: { maps?: { places?: unknown } } }).google?.maps?.places as
          | { Autocomplete: new (input: HTMLInputElement, options: unknown) => unknown }
          | undefined;

        if (!isMounted || !places || !inputRef.current) return;

        const autocomplete = new places.Autocomplete(inputRef.current, {
          types: placeTypesKey.split(','),
          componentRestrictions: { country: ['fr'] },
          fields: ['address_components'],
        }) as { addListener: (event: string, handler: () => void) => void; getPlace: () => unknown };

        autocomplete.addListener('place_changed', () => {
          const place = autocomplete.getPlace() as { address_components?: GoogleAddressComponent[] };
          const parsed = parseGoogleAddress(place.address_components ?? []);

          onAddressSelected(parsed);
          onChange(toLabel(parsed));
        });
      })
      .catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [onAddressSelected, onChange, placeTypesKey, toLabel]);

  return (
    <TextField
      id={id}
      inputRef={inputRef}
      fullWidth
      size={size}
      variant="outlined"
      label={label}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={error}
      helperText={helperText}
      inputProps={{ name: 'address' }}
    />
  );
};

export default AddressAutocomplete;
